/**
 * settingsV2Demo — dataset único de la vista previa "Settings V2".
 *
 * Prototipo visual del futuro producto hostelero descrito en
 * `docs/PRODUCT_V2_SPEC.md`. Igual que `pmsDemoData.js`, este fichero es la
 * ÚNICA fuente de datos: ninguna pantalla inventa valores por su cuenta y
 * nada de esto toca Mongo, `project_settings` ni ningún endpoint.
 *
 * Dos tipos de valor conviven aquí y el UI los distingue:
 *
 *   `approved: true`  → decisión de producto ya aprobada
 *                       (docs/PRODUCT_DECISIONS.md · docs/PMS_DEMO_SPEC.md):
 *                       cuota de 60 noches por propietario, preaviso de 30
 *                       días, blackout MotoGP, IVA 10 %, management 15 %
 *                       después de costes, fee directo 5 %, comisiones OTA
 *                       modeladas, pesos de fairness 70/30 y horizonte 70/30.
 *
 *   sin `approved`    → valor de EJEMPLO de la vista previa. Estancia mínima,
 *                       lead time, cut-off, duración del hold o política de
 *                       cancelación NO tienen decisión aprobada; se muestran
 *                       para poder juzgar la interfaz, no para fijar producto.
 *
 * Vocabulario (PRODUCT_V2_SPEC §2.1): "tipo de alojamiento" (unit type) es la
 * categoría comercial que se vende; "grupo de reparto" (allocation pool) es la
 * agrupación OPCIONAL que usa el motor de reparto. En Dolce coinciden 1:1,
 * pero son entidades distintas y el prototipo las muestra por separado.
 */

// ---------------------------------------------------------------------------
// Marca de la vista previa
// ---------------------------------------------------------------------------

export const SETTINGS_DEMO_BANNER = {
  title: "Vista previa · Datos de demostración",
  subtitle:
    "Prototipo de la configuración del producto hostelero V2. Nada de lo que edites aquí se guarda ni afecta a producción",
};

/** Etiqueta reutilizable para los valores que sí vienen de una decisión aprobada. */
export const APPROVED_LABEL = "Decisión aprobada";

// ---------------------------------------------------------------------------
// 1. Propiedad
// ---------------------------------------------------------------------------

export const PROPERTY_TYPES = [
  { value: "villa_complex", label: "Complejo de villas" },
  { value: "boutique_hotel", label: "Hotel boutique" },
  { value: "resort", label: "Resort" },
  { value: "homestay", label: "Homestay" },
];

/**
 * Etiqueta de unidad — la demostración más directa de que el producto adapta
 * su vocabulario al cliente. Dolce usa "Villa"; un hotel usaría "Habitación".
 */
export const UNIT_LABELS = [
  { value: "villa", singular: "Villa", plural: "Villas" },
  { value: "room", singular: "Habitación", plural: "Habitaciones" },
  { value: "bungalow", singular: "Bungalow", plural: "Bungalows" },
  { value: "apartment", singular: "Apartamento", plural: "Apartamentos" },
  { value: "custom", singular: "Personalizado", plural: "Personalizados" },
];

export const TIMEZONES = [
  "Asia/Makassar",
  "Asia/Jakarta",
  "Asia/Jayapura",
  "Europe/Madrid",
  "UTC",
];

export const CURRENCIES = ["EUR", "IDR", "USD", "AUD", "SGD"];

export const LANGUAGES = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
];

export const PROPERTY = {
  name: "Dolce Villas Lombok",
  type: "villa_complex",
  internal_code: "DVL-01",
  description:
    "Complejo boutique de 10 villas privadas con piscina en Kuta, Lombok. Dos categorías comerciales: Dolce Vistas y Dolce Privè.",
  country: "Indonesia",
  region: "Lombok",
  city: "Kuta",
  address: "Jl. Raya Kuta, Kuta, Lombok Tengah, Nusa Tenggara Barat",
  timezone: "Asia/Makassar",
  currency_operational: "IDR",
  currency_reporting: "EUR",
  languages: ["es", "en", "id"],
  default_guest_language: "en",
  unit_label: "villa",
  unit_label_custom: "",
  check_in_time: "14:00",
  check_out_time: "11:00",
  contact_phone: "+62 812 0000 0000",
  contact_whatsapp: "+62 812 0000 0000",
  contact_email: "hola@dolcevillaslombok.com",
  logo_set: true,
  cover_set: false,
};

// ---------------------------------------------------------------------------
// 2. Tipos de alojamiento · unidades · grupos de reparto
// ---------------------------------------------------------------------------

/**
 * Tipo de alojamiento (unit type): categoría COMERCIAL que se vende.
 * Lleva tarifa, capacidad, contenido y mapeo de canal.
 */
export const UNIT_TYPES = [
  {
    unit_type_id: "vistas",
    name: "Dolce Vistas",
    description: "Villa de 3 dormitorios con piscina privada y vistas al valle.",
    unit_count: 6,
    sleeps: 6,
    bedrooms: 3,
    base_rate_eur: 200,
    amenities_count: 18,
    photos_count: 24,
    website_visible: true,
    accent: "#9C5236",
  },
  {
    unit_type_id: "prive",
    name: "Dolce Privè",
    description: "Villa de 4 dormitorios, parcela ampliada y acceso reservado.",
    unit_count: 4,
    sleeps: 8,
    bedrooms: 4,
    base_rate_eur: 260,
    amenities_count: 21,
    photos_count: 19,
    website_visible: true,
    accent: "#3C3A55",
  },
];

/** Unidad física: lo que se reserva, se limpia y tiene propietario. */
export const UNITS = [
  { unit_id: "V01", name: "Villa Ombak", unit_type_id: "vistas", status: "active", owner_assigned: true },
  { unit_id: "V02", name: "Villa Purnama", unit_type_id: "vistas", status: "active", owner_assigned: true },
  { unit_id: "V03", name: "Villa Bintang", unit_type_id: "vistas", status: "active", owner_assigned: true },
  { unit_id: "V04", name: "Villa Cahaya", unit_type_id: "vistas", status: "active", owner_assigned: false },
  { unit_id: "V05", name: "Villa Melati", unit_type_id: "vistas", status: "active", owner_assigned: true },
  { unit_id: "V06", name: "Villa Anggrek", unit_type_id: "vistas", status: "maintenance", owner_assigned: true },
  { unit_id: "V07", name: "Villa Cempaka", unit_type_id: "prive", status: "active", owner_assigned: true },
  { unit_id: "V08", name: "Villa Kenanga", unit_type_id: "prive", status: "active", owner_assigned: true },
  { unit_id: "V09", name: "Villa Seruni", unit_type_id: "prive", status: "active", owner_assigned: false },
  { unit_id: "V10", name: "Villa Tanjung", unit_type_id: "prive", status: "active", owner_assigned: true },
];

export const UNIT_STATUS_LABEL = {
  active: "Operativa",
  maintenance: "En mantenimiento",
  inactive: "Fuera de servicio",
};

/**
 * Grupo de reparto (allocation pool): agrupación OPCIONAL de unidades físicas
 * que compiten en el reparto justo. Para Dolce hay uno por tipo (1:1), pero el
 * modelo no exige esa correspondencia (PRODUCT_V2_SPEC §2.1).
 */
export const ALLOCATION_POOLS = [
  {
    pool_id: "pool_vistas",
    name: "Grupo Dolce Vistas",
    unit_type_id: "vistas",
    unit_ids: ["V01", "V02", "V03", "V04", "V05", "V06"],
    active: true,
  },
  {
    pool_id: "pool_prive",
    name: "Grupo Dolce Privè",
    unit_type_id: "prive",
    unit_ids: ["V07", "V08", "V09", "V10"],
    active: true,
  },
];

/** Pesos aprobados del motor de reparto (PRODUCT_DECISIONS.md). */
export const ALLOCATION_ADVANCED = {
  enabled: true,
  weight_revenue: 70,
  weight_occupancy: 30,
  horizon_current_month: 70,
  horizon_ytd: 30,
  tie_breakers: [
    "Menos noches futuras ya reservadas",
    "Más tiempo desde la última asignación",
    "Identificador de unidad (determinista)",
  ],
  approved: true,
};

// ---------------------------------------------------------------------------
// 3. Reglas de reserva
// ---------------------------------------------------------------------------

export const CANCELLATION_POLICIES = [
  { value: "flexible", label: "Flexible · cancelación gratuita hasta 7 días antes" },
  { value: "moderate", label: "Moderada · cancelación gratuita hasta 14 días antes" },
  { value: "strict", label: "Estricta · cancelación gratuita hasta 30 días antes" },
  { value: "custom", label: "Personalizada" },
];

export const BOOKING_RULES = {
  min_stay_nights: 2,
  max_stay_nights: 30,
  lead_time_days: 1,
  cutoff_hour: "18:00",
  hold_duration_hours: 48,
  cancellation_policy: "moderate",
  // La reserva directa vive en `website.direct_booking_enabled` y el reparto
  // automático en `allocation.enabled`: un solo sitio por ajuste, que es
  // justo lo que el producto predica. Reglas de reserva los lee de ahí.
  owner_stays_enabled: true,
  owner_stay_quota_nights: 60,
  owner_stay_min_notice_days: 30,
  owner_stay_blackout: ["MotoGP Mandalika · 15–22 oct 2028"],
};

/** Campos de reglas de reserva que provienen de una decisión aprobada. */
export const BOOKING_RULES_APPROVED_FIELDS = [
  "owner_stay_quota_nights",
  "owner_stay_min_notice_days",
  "owner_stay_blackout",
];

// ---------------------------------------------------------------------------
// 4. Requisitos del huésped
// ---------------------------------------------------------------------------

export const GUEST_FIELD_LABELS = {
  full_name: "Nombre completo",
  email: "Email",
  phone: "Teléfono",
  nationality: "Nacionalidad",
  passport: "Pasaporte / documento de identidad",
  date_of_birth: "Fecha de nacimiento",
  arrival_time: "Hora de llegada",
  flight_number: "Número de vuelo",
};

export const GUEST_REQUIREMENTS = {
  online_checkin_enabled: true,
  online_checkin_required: true,
  fields: {
    full_name: true,
    email: true,
    phone: true,
    nationality: true,
    passport: true,
    date_of_birth: false,
    arrival_time: true,
    flight_number: false,
  },
  immigration_reporting_required: true,
  immigration_workflow: "future",
  consent_house_rules: true,
  consent_privacy: true,
  document_retention: "pending",
};

// ---------------------------------------------------------------------------
// 5. Servicios (upsells)
// ---------------------------------------------------------------------------

export const SERVICE_CATEGORIES = [
  { value: "breakfast", label: "Desayuno" },
  { value: "transport", label: "Transporte" },
  { value: "rental", label: "Alquiler" },
  { value: "welcome", label: "Bienvenida" },
  { value: "laundry", label: "Lavandería" },
  { value: "wellness", label: "Bienestar" },
  { value: "activity", label: "Experiencias" },
  { value: "timing", label: "Horarios" },
  { value: "other", label: "Otros" },
];

export const PRICING_UNITS = [
  { value: "per_stay", label: "Por estancia" },
  { value: "per_person", label: "Por persona" },
  { value: "per_day", label: "Por día" },
  { value: "per_trip", label: "Por trayecto" },
  { value: "per_unit", label: "Por unidad" },
];

export const SERVICES = [
  {
    service_id: "svc_breakfast",
    name: "Desayuno",
    category: "breakfast",
    description: "Desayuno balinés o continental servido en la villa.",
    icon: "ForkKnife",
    image_set: true,
    cost_price_eur: 6,
    sale_price_eur: 14,
    tax_included: true,
    pricing_unit: "per_person",
    supplier: "Cocina interna",
    lead_time_hours: 12,
    availability: "Todos los días · 07:00–10:30",
    active: true,
  },
  {
    service_id: "svc_airport",
    name: "Traslado aeropuerto",
    category: "transport",
    description: "Recogida privada en el aeropuerto de Lombok (LOP).",
    icon: "Car",
    image_set: true,
    cost_price_eur: 22,
    sale_price_eur: 38,
    tax_included: true,
    pricing_unit: "per_trip",
    supplier: "Bali Trans Lombok",
    lead_time_hours: 24,
    availability: "Todos los días · 24 h",
    active: true,
  },
  {
    service_id: "svc_scooter",
    name: "Alquiler de scooter",
    category: "rental",
    description: "Scooter 125cc con dos cascos y entrega en la villa.",
    icon: "Scooter",
    image_set: true,
    cost_price_eur: 4,
    sale_price_eur: 9,
    tax_included: true,
    pricing_unit: "per_day",
    supplier: "Kuta Rentals",
    lead_time_hours: 6,
    availability: "Todos los días",
    active: true,
  },
  {
    service_id: "svc_welcome",
    name: "Welcome Pack",
    category: "welcome",
    description: "Cesta de bienvenida con fruta local, agua y snacks.",
    icon: "Gift",
    image_set: false,
    cost_price_eur: 9,
    sale_price_eur: 20,
    tax_included: true,
    pricing_unit: "per_stay",
    supplier: "Cocina interna",
    lead_time_hours: 24,
    availability: "Con reserva confirmada",
    active: true,
  },
  {
    service_id: "svc_massage",
    name: "Masaje en la villa",
    category: "wellness",
    description: "Masaje balinés de 60 o 90 minutos en la terraza.",
    icon: "Flower",
    image_set: true,
    cost_price_eur: 14,
    sale_price_eur: 32,
    tax_included: true,
    pricing_unit: "per_person",
    supplier: "Sasak Spa",
    lead_time_hours: 8,
    availability: "Todos los días · 10:00–19:00",
    active: true,
  },
  {
    service_id: "svc_laundry",
    name: "Lavandería",
    category: "laundry",
    description: "Servicio de lavado y planchado con entrega en 24 h.",
    icon: "TShirt",
    image_set: true,
    cost_price_eur: 3,
    sale_price_eur: 8,
    tax_included: true,
    pricing_unit: "per_unit",
    supplier: "Lombok Laundry",
    lead_time_hours: 24,
    availability: "Lunes a sábado",
    active: false,
  },
];

// ---------------------------------------------------------------------------
// 6. Finanzas e impuestos
// ---------------------------------------------------------------------------

/**
 * Valores aprobados en PRODUCT_DECISIONS.md. La fórmula se muestra como
 * configuración explicada, no como contabilidad.
 */
export const FINANCE = {
  currency_reporting: "EUR",
  currency_operational: "IDR",
  fx_note: "Conversión IDR→EUR con la constante única de la demo (17 500 IDR/EUR).",
  tax_pct: 10,
  tax_base: "Ingresos de alojamiento",
  // Solo el fee de canal directo es decisión aprobada; las comisiones de OTA
  // son supuestos de modelado (PRODUCT_DECISIONS.md lo dice con esas palabras).
  channel_costs: [
    { key: "booking", label: "Booking.com", pct: 18, approved: false },
    { key: "airbnb", label: "Airbnb", pct: 3, approved: false },
    { key: "direct_web", label: "Web propia (fee directo)", pct: 5, approved: true },
  ],
  payment_processing_pct: 2.8,
  payment_processing_fixed_idr: 2000,
  payment_processing_note:
    "Solo canales directos con pago por tarjeta. Las OTA no pasan por nuestra pasarela. Pasarela definitiva por decidir.",
  management_fee_pct: 15,
  management_base_order: [
    "Ingresos de alojamiento",
    "− Impuestos",
    "− Coste de canal",
    "− Procesamiento de pago",
    "− Gastos operativos",
  ],
  owner_statement_day: 5,
  owner_statement_note:
    "Los gastos operativos son un importe mensual variable; hasta el cierre se muestran como estimados.",
};

// ---------------------------------------------------------------------------
// 7. Equipo (maqueta — no toca usuarios reales)
// ---------------------------------------------------------------------------

export const TEAM = [
  { member_id: "tm_1", name: "Ana Prasetya", role: "property_admin", scope: "Dolce Villas Lombok", status: "active", email: "ana@ejemplo.demo", phone: "+62 811 111 111" },
  { member_id: "tm_2", name: "Budi Santoso", role: "manager", scope: "Dolce Villas Lombok", status: "active", email: "budi@ejemplo.demo", phone: "+62 811 222 222" },
  { member_id: "tm_3", name: "Citra Dewi", role: "reception", scope: "Dolce Villas Lombok", status: "active", email: "citra@ejemplo.demo", phone: "+62 811 333 333" },
  { member_id: "tm_4", name: "Dewi Lestari", role: "housekeeping", scope: "Vistas · V01–V06", status: "active", email: "dewi@ejemplo.demo", phone: "+62 811 444 444" },
  { member_id: "tm_5", name: "Eka Wijaya", role: "maintenance", scope: "Dolce Villas Lombok", status: "invited", email: "eka@ejemplo.demo", phone: "+62 811 555 555" },
];

export const TEAM_STATUS_LABEL = {
  active: "Activo",
  invited: "Invitación enviada",
  inactive: "Inactivo",
};

// ---------------------------------------------------------------------------
// 8. Roles conceptuales (PRODUCT_V2_SPEC §14) — NO tocan auth
// ---------------------------------------------------------------------------

export const PERMISSION_AREAS = [
  { key: "today", label: "Hoy" },
  { key: "bookings", label: "Reservas" },
  { key: "guests", label: "Huéspedes" },
  { key: "operations", label: "Operaciones" },
  { key: "finance", label: "Finanzas" },
  { key: "reports", label: "Informes" },
  { key: "owners", label: "Propietarios" },
  { key: "settings", label: "Configuración" },
];

/** none · read · write · full */
export const ROLE_TEMPLATES = [
  {
    role_key: "property_admin",
    label: "Administrador",
    summary: "Administra el negocio completo: configuración, equipo, finanzas y cierre mensual.",
    permissions: { today: "full", bookings: "full", guests: "full", operations: "full", finance: "full", reports: "full", owners: "full", settings: "full" },
  },
  {
    role_key: "manager",
    label: "Manager",
    summary: "Opera el día a día, puede forzar una asignación con motivo y cerrar el mes.",
    permissions: { today: "full", bookings: "full", guests: "full", operations: "full", finance: "write", reports: "read", owners: "write", settings: "read" },
  },
  {
    role_key: "reception",
    label: "Recepción",
    summary: "Llegadas, salidas, check-in y comunicación con el huésped. Sin datos financieros.",
    permissions: { today: "full", bookings: "write", guests: "full", operations: "read", finance: "none", reports: "none", owners: "none", settings: "none" },
  },
  {
    role_key: "housekeeping",
    label: "Housekeeping",
    summary: "Solo sus tareas de limpieza y el estado de las unidades, desde el móvil.",
    permissions: { today: "read", bookings: "none", guests: "none", operations: "write", finance: "none", reports: "none", owners: "none", settings: "none" },
  },
  {
    role_key: "maintenance",
    label: "Mantenimiento",
    summary: "Incidencias asignadas, fotos y coste. Sin datos personales del huésped.",
    permissions: { today: "read", bookings: "none", guests: "none", operations: "write", finance: "none", reports: "none", owners: "none", settings: "none" },
  },
  {
    role_key: "finance",
    label: "Finanzas",
    summary: "Libro operativo, gastos, facturas y cierre mensual. Reservas en solo lectura.",
    permissions: { today: "read", bookings: "read", guests: "none", operations: "none", finance: "full", reports: "full", owners: "read", settings: "none" },
  },
  {
    role_key: "unit_owner",
    label: "Propietario",
    summary: "Solo sus unidades: rendimiento, extractos y solicitudes de estancia propia.",
    permissions: { today: "none", bookings: "read", guests: "none", operations: "none", finance: "none", reports: "read", owners: "read", settings: "none" },
  },
];

export const PERMISSION_LEVEL_LABEL = {
  none: "—",
  read: "Ver",
  write: "Editar",
  full: "Total",
};

// ---------------------------------------------------------------------------
// 9. Canales de venta
// ---------------------------------------------------------------------------

/**
 * `via_channel_manager` marca los canales que en el futuro se conectarán a
 * través de un proveedor de channel manager, no directamente.
 */
export const SALES_CHANNELS = [
  { channel_key: "airbnb", label: "Airbnb", kind: "ota", via_channel_manager: true, status: "not_connected", note: "Se conectará mediante el channel manager." },
  { channel_key: "booking", label: "Booking.com", kind: "ota", via_channel_manager: true, status: "not_connected", note: "Se conectará mediante el channel manager." },
  { channel_key: "agoda", label: "Agoda", kind: "ota", via_channel_manager: true, status: "not_connected", note: "Se conectará mediante el channel manager." },
  { channel_key: "direct_web", label: "Web propia", kind: "direct", via_channel_manager: false, status: "setup_required", note: "Disponible cuando se publique la web y se active la reserva directa." },
  { channel_key: "manual", label: "Reserva manual", kind: "direct", via_channel_manager: false, status: "connected_demo", note: "Alta manual desde el PMS." },
  { channel_key: "whatsapp", label: "WhatsApp", kind: "direct", via_channel_manager: false, status: "connected_demo", note: "Conversaciones y reservas asistidas por el equipo." },
];

export const CHANNEL_STATUS = {
  connected_demo: { label: "Conectado (demo)", tone: "emerald" },
  not_connected: { label: "Sin conectar", tone: "stone" },
  setup_required: { label: "Requiere configuración", tone: "amber" },
};

// ---------------------------------------------------------------------------
// 10. Web
// ---------------------------------------------------------------------------

export const WEBSITE_STYLES = [
  { value: "editorial", label: "Editorial", hint: "Tipografía serif, mucho aire, foto grande." },
  { value: "minimal", label: "Minimalista", hint: "Retícula limpia, sin adornos." },
  { value: "warm", label: "Cálido", hint: "Tonos tierra, cercano, familiar." },
];

export const WEBSITE_TONES = [
  { value: "calm", label: "Sereno" },
  { value: "premium", label: "Premium" },
  { value: "friendly", label: "Cercano" },
];

export const WEBSITE = {
  logo_set: true,
  brand_primary: "#9C5236",
  brand_ink: "#1C1917",
  style: "editorial",
  tone: "calm",
  hero_set: false,
  sections: { units: true, services: true, location: true, gallery: true, reviews: false, faq: true, contact: true },
  languages: ["es", "en"],
  seo_title: "Dolce Villas Lombok · Villas privadas en Kuta",
  seo_description: "Diez villas privadas con piscina en Kuta, Lombok.",
  domain: "dolcevillaslombok.com",
  domain_status: "not_configured",
  direct_booking_enabled: false,
};

/** Lo que la web ya podría tomar de la configuración existente. */
export const WEBSITE_CONTENT_SOURCES = [
  { key: "property", label: "Información de la propiedad", section: "property" },
  { key: "unit_types", label: null, section: "units" }, // se compone con el vocabulario
  { key: "photos", label: null, section: "units" }, // recuento real de los tipos
  { key: "amenities", label: "Equipamiento", section: "units" },
  { key: "services", label: "Servicios", section: "services" },
  { key: "policies", label: "Políticas", section: "booking" },
  { key: "location", label: "Ubicación", section: "property" },
];

export const WEBSITE_AI_STEPS = [
  { key: "configure", label: "Configurar", detail: "La información que ya has rellenado en Ajustes." },
  { key: "generate", label: "Generar", detail: "La IA propone estructura y textos a partir de esos datos." },
  { key: "preview", label: "Previsualizar", detail: "Escritorio y móvil, antes de publicar nada." },
  { key: "edit", label: "Editar", detail: "Ajustas lo que quieras; nada se publica solo." },
  { key: "publish", label: "Publicar", detail: "Dominio propio y reserva directa cuando decidas." },
];

// ---------------------------------------------------------------------------
// 11. Notificaciones (nivel propiedad, no permisos del navegador)
// ---------------------------------------------------------------------------

export const NOTIFICATION_EVENTS = [
  { key: "reservation_new", label: "Nueva reserva", hint: "Cuando entra una reserva por cualquier canal." },
  { key: "arrival_tomorrow", label: "Llegada mañana", hint: "Resumen de las llegadas del día siguiente." },
  { key: "checkin_incomplete", label: "Check-in incompleto", hint: "El huésped llega sin completar el check-in online." },
  { key: "housekeeping_overdue", label: "Limpieza retrasada", hint: "Una unidad no está lista a tiempo para la llegada." },
  { key: "issue_opened", label: "Incidencia abierta", hint: "Se registra una incidencia nueva." },
  { key: "blocking_maintenance", label: "Mantenimiento bloqueante", hint: "Una incidencia bloquea la disponibilidad." },
  { key: "payment_issue", label: "Problema de pago", hint: "Un cobro falla o queda pendiente." },
  { key: "monthly_close", label: "Recordatorio de cierre mensual", hint: "Unos días antes del cierre del mes." },
];

export const NOTIFICATION_CHANNELS = [
  { key: "push", label: "Push" },
  { key: "email", label: "Email" },
  { key: "whatsapp", label: "WhatsApp" },
];

export const NOTIFICATIONS = {
  reservation_new: { push: true, email: true, whatsapp: false },
  arrival_tomorrow: { push: true, email: false, whatsapp: true },
  checkin_incomplete: { push: true, email: false, whatsapp: false },
  housekeeping_overdue: { push: true, email: false, whatsapp: false },
  issue_opened: { push: true, email: true, whatsapp: false },
  blocking_maintenance: { push: true, email: true, whatsapp: true },
  payment_issue: { push: false, email: true, whatsapp: false },
  monthly_close: { push: false, email: true, whatsapp: false },
};

// ---------------------------------------------------------------------------
// 12. Integraciones
// ---------------------------------------------------------------------------

/**
 * `real: true`  → integración que la plataforma YA usa hoy en producción.
 * `real: false` → capacidad futura del producto hostelero. Ningún proveedor
 *                 futuro aparece como conectado.
 */
export const INTEGRATIONS = [
  {
    key: "channel_manager",
    label: "Channel manager",
    purpose: "Distribución en OTA: reservas, disponibilidad y tarifas.",
    status: "coming_later",
    real: false,
    provider_note: "Proveedor por decidir: todavía no hay ninguno elegido ni contratado.",
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    purpose: "Conversaciones con leads y, en el futuro, con huéspedes.",
    status: "connected",
    real: true,
    provider_note: "Integración real en uso hoy por el CRM.",
  },
  {
    key: "email",
    label: "Email transaccional",
    purpose: "Confirmaciones, invitaciones y extractos.",
    status: "connected",
    real: true,
    provider_note: "Integración real en uso hoy.",
  },
  {
    key: "push",
    label: "Notificaciones push",
    purpose: "Avisos operativos al equipo en el móvil.",
    status: "connected",
    real: true,
    provider_note: "Integración real en uso hoy.",
  },
  {
    key: "ai",
    label: "Inteligencia artificial",
    purpose: "Priorización, resúmenes y borradores de mensaje.",
    status: "connected",
    real: true,
    provider_note: "Integración real en uso hoy.",
  },
  {
    key: "pricing",
    label: "Precios dinámicos",
    purpose: "Recomendación de tarifa por tipo de alojamiento y fecha.",
    status: "coming_later",
    real: false,
    provider_note: "Capacidad futura del producto.",
  },
  {
    key: "payments",
    label: "Pagos",
    purpose: "Cobros de reserva directa y de servicios.",
    status: "needs_setup",
    real: false,
    provider_note: "Pasarela por decidir.",
  },
];

export const INTEGRATION_STATUS = {
  connected: { label: "Conectado", tone: "emerald" },
  needs_setup: { label: "Requiere configuración", tone: "amber" },
  coming_later: { label: "Más adelante", tone: "stone" },
};

// ---------------------------------------------------------------------------
// 13. "Configurar una vez" — dónde se reutiliza cada dato
// ---------------------------------------------------------------------------

/**
 * El corazón conceptual del producto: un dato se configura una vez y alimenta
 * varias superficies. El UI lo muestra de forma discreta ("Usado en N sitios")
 * y solo en los campos donde enseña algo.
 */
export const USED_IN = {
  property_name: ["Web", "Portal del huésped", "Informes", "Emails", "Extractos"],
  unit_label: ["PMS", "Portal del huésped", "Web", "Housekeeping", "Informes"],
  unit_types: ["Reservas", "Web", "Channel manager", "Portal del huésped", "Informes"],
  units: ["Calendario", "Housekeeping", "Reparto", "Informes", "Extractos"],
  services: ["Portal del huésped", "Ficha de reserva", "Operaciones", "Transacciones", "Informes"],
  check_times: ["Portal del huésped", "Housekeeping", "Web", "Emails"],
  finance: ["Extractos de propietario", "Informes", "Cierre mensual"],
  guest_requirements: ["Portal del huésped", "Check-in", "Inmigración"],
  languages: ["Portal del huésped", "Web", "Emails"],
};

// ---------------------------------------------------------------------------
// 14. Índice de secciones
// ---------------------------------------------------------------------------

export const SETTINGS_SECTIONS = [
  { key: "property", label: "Propiedad", icon: "Buildings", hint: "Identidad, ubicación y vocabulario" },
  { key: "units", label: "Alojamientos", icon: "House", hint: "Tipos, unidades y reparto" },
  { key: "booking", label: "Reglas de reserva", icon: "CalendarCheck", hint: "Estancias, holds y estancias del propietario" },
  { key: "guests", label: "Requisitos del huésped", icon: "IdentificationCard", hint: "Check-in online e inmigración" },
  { key: "services", label: "Servicios", icon: "Sparkle", hint: "Extras que se venden al huésped" },
  { key: "finance", label: "Finanzas e impuestos", icon: "Coins", hint: "Impuestos, comisiones y liquidación" },
  { key: "team", label: "Equipo", icon: "UsersThree", hint: "Personas con acceso" },
  { key: "roles", label: "Roles", icon: "ShieldCheck", hint: "Plantillas de permisos" },
  { key: "channels", label: "Canales", icon: "ShareNetwork", hint: "Dónde se vende" },
  { key: "website", label: "Web", icon: "Globe", hint: "Marca, contenido y dominio" },
  { key: "notifications", label: "Notificaciones", icon: "BellSimple", hint: "Qué avisa y por dónde" },
  { key: "integrations", label: "Integraciones", icon: "PlugsConnected", hint: "Servicios conectados" },
];
